/**
 * Shift Analysis Service
 *
 * Fetches a pre-analysed picture of ONE shift on ONE date for ONE line, across
 * all machines the user is allowed to see. All analysis (fault durations,
 * severities, machine statuses, rollups) is done server-side — the frontend
 * only fetches and renders.
 *
 * Powered by 4 statements (2 data + 2 insight):
 *   PC.shift_timeline                → fault events (one row per fault interval)
 *   PC.shift_timeline_insight        → headline KPIs (single row)
 *   PC.shift_machine_summary         → per-machine scorecard (worst first)
 *   PC.shift_machine_summary_insight → line rollup (single row)
 *
 * All four take the same parameters: { line_bk, shift_name, shift_date }.
 */

import { orgNatsClient, StatementIds } from './OrgNATSClient';

export type FaultSeverity = 'critical' | 'major' | 'minor';
export type FaultRating = 'int' | 'ext' | 'undefined';
export type MachineStatus = 'good' | 'watch' | 'bad' | 'no_data';

/** One fault/stoppage interval (PC.shift_timeline). */
export interface FaultEvent {
  machine_sn: string;
  machine_name: string;
  event_start: string; // "YYYY-MM-DD HH:MM:SS"
  event_end: string;
  duration_s: number;
  rating: FaultRating;
  fault_text: string;
  severity: FaultSeverity;
}

/** One ranked internal stop cause (PC.shift_stop_causes, worst first). */
export interface StopCause {
  machine_sn: string;
  machine_name: string;
  fault_text: string;
  occurrences: number;
  total_seconds: number;
  longest_seconds: number;
}

/** Per-machine internal-stop rollup (PC.shift_stop_causes_insight, worst first). */
export interface CulpritMachine {
  machine_sn: string;
  machine_name: string;
  internal_stops: number;
  internal_seconds: number;
  distinct_faults: number;
}

export type StateCategory =
  | 'running'
  | 'waiting_ext'
  | 'waiting_int'
  | 'standby'
  | 'setup'
  | 'stopped';

/** One operating-state interval (PC.shift_status_timeline). */
export interface StateInterval {
  machine_sn: string;
  machine_name: string;
  state_start: string;
  state_end: string;
  duration_s: number;
  state_text: string;
  state_category: StateCategory;
}

/** Per-machine seconds in a state category (PC.shift_status_timeline_insight). */
export interface StateBreakdownRow {
  machine_sn: string;
  machine_name: string;
  state_category: StateCategory;
  total_seconds: number;
  occurrences: number;
}

/** One (blocked, blocker) attribution row (PC.shift_stop_attribution). */
export interface BlockerRow {
  blocked_machine_sn: string;
  blocked_machine_name: string;
  blocker_machine_sn: string;
  blocker_machine_name: string;
  blocked_seconds: number;
  episodes: number;
  first_blocked_at: string;
  blocker_down_from: string;
}

/** Timeline headline KPIs (PC.shift_timeline_insight, single row). */
export interface TimelineSummary {
  total_faults: number;
  total_fault_seconds: number;
  internal_fault_seconds: number;
  external_fault_seconds: number;
  critical_count: number;
  major_count: number;
  minor_count: number;
  longest_fault_seconds: number;
  machines_with_faults: number;
}

/** Per-machine scorecard row (PC.shift_machine_summary, worst first). */
export interface MachineSummaryRow {
  machine_sn: string;
  machine_name: string;
  output: number;
  waste: number;
  reject_rate: number;
  efficiency: number; // 0–1
  utilization: number; // 0–1
  downtime_ms: number;
  mtbf: number;
  status: MachineStatus;
}

/** Line rollup (PC.shift_machine_summary_insight, single row). */
export interface LineSummary {
  machine_count: number;
  avg_efficiency: number;
  min_efficiency: number;
  max_efficiency: number;
  avg_utilization: number;
  total_waste: number;
  total_downtime_ms: number;
  good_count: number;
  watch_count: number;
  bad_count: number;
  no_data_count: number;
}

/** Lost production grouped by stop cause (PC.shift_lost_production). */
export interface LostCause {
  cause: string;
  rating: string; // "Int" | "Ext"
  lost_production: number;
  stops: number;
  total_stops_ms: number;
}

/** Production lost to reduced speed, by rating (PC.shift_speed_losses). */
export interface SpeedLoss {
  rating: string; // "Int" | "Ext"
  duration_ms: number;
  good_production: number;
  theoretical_prod: number;
  lost_production: number;
}

/** One point of the per-shift KPI curve (PC.shift_kpi_timeline). */
export interface KpiPoint {
  t: number; // ms
  efficiency: number | null; // 0..1
  utilization: number | null; // 0..1
}

export interface ShiftAnalysis {
  lineBk: string;
  shiftName: string;
  shiftDate: string;
  /** Ranked internal (root-cause) stops, worst first. */
  stopCauses: StopCause[];
  /** Per-machine internal-stop rollup, worst first ([0] = main culprit). */
  culpritMachines: CulpritMachine[];
  /** machine_sn → whole-shift operating-state intervals (the headline Gantt). */
  stateLanes: Map<string, StateInterval[]>;
  /** machine_sn → per-category second totals (availability stacked bar). */
  stateBreakdown: Map<string, StateBreakdownRow[]>;
  /** blocked_machine_sn → blocker rows, worst first ([0] = dominant blocker). */
  blockers: Map<string, BlockerRow[]>;
  /** All fault events, ordered by event_start (as delivered by the backend). */
  events: FaultEvent[];
  /** machine_sn → that machine's fault events (lanes for the timeline). */
  lanes: Map<string, FaultEvent[]>;
  timelineSummary: TimelineSummary | null;
  machines: MachineSummaryRow[];
  lineSummary: LineSummary | null;
  /** Lost production by cause (optional — empty when the machine has no agg data). */
  lostCauses: LostCause[];
  /** Production lost to reduced speed, by rating (optional). */
  speedLosses: SpeedLoss[];
  /** Per-minute efficiency/utilization curve over the shift (optional). */
  kpiTimeline: KpiPoint[];
}

const TIMEOUT = 30000;

/** Normalise a NATS response body into a flat array of rows. */
function asRows(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (data && typeof data === 'object') {
    const results = (data as { results?: unknown }).results;
    if (Array.isArray(results)) return results as Record<string, unknown>[];
  }
  return [];
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function str(v: unknown, fallback = ''): string {
  return v === null || v === undefined ? fallback : String(v);
}

function parseFaultEvent(r: Record<string, unknown>): FaultEvent {
  const severity = str(r.severity, 'minor') as FaultSeverity;
  const rating = str(r.rating, 'undefined') as FaultRating;
  return {
    machine_sn: str(r.machine_sn),
    machine_name: str(r.machine_name, str(r.machine_sn)),
    event_start: str(r.event_start),
    event_end: str(r.event_end),
    duration_s: num(r.duration_s),
    rating: rating === 'int' || rating === 'ext' ? rating : 'undefined',
    // `fault_text` is the new field name; `fault_group` kept as a fallback.
    fault_text: str(r.fault_text, str(r.fault_group, 'N/A')),
    severity:
      severity === 'critical' || severity === 'major' ? severity : 'minor',
  };
}

function parseStopCause(r: Record<string, unknown>): StopCause {
  return {
    machine_sn: str(r.machine_sn),
    machine_name: str(r.machine_name, str(r.machine_sn)),
    fault_text: str(r.fault_text, 'N/A'),
    occurrences: num(r.occurrences),
    total_seconds: num(r.total_seconds),
    longest_seconds: num(r.longest_seconds),
  };
}

function parseCulprit(r: Record<string, unknown>): CulpritMachine {
  return {
    machine_sn: str(r.machine_sn),
    machine_name: str(r.machine_name, str(r.machine_sn)),
    internal_stops: num(r.internal_stops),
    internal_seconds: num(r.internal_seconds),
    distinct_faults: num(r.distinct_faults),
  };
}

const STATE_CATEGORIES: StateCategory[] = [
  'running',
  'waiting_ext',
  'waiting_int',
  'standby',
  'setup',
  'stopped',
];

function toCategory(v: unknown): StateCategory {
  const s = str(v, 'stopped') as StateCategory;
  return STATE_CATEGORIES.includes(s) ? s : 'stopped';
}

function parseStateInterval(r: Record<string, unknown>): StateInterval {
  return {
    machine_sn: str(r.machine_sn),
    machine_name: str(r.machine_name, str(r.machine_sn)),
    state_start: str(r.state_start),
    state_end: str(r.state_end),
    duration_s: num(r.duration_s),
    state_text: str(r.state_text),
    state_category: toCategory(r.state_category),
  };
}

function parseStateBreakdown(r: Record<string, unknown>): StateBreakdownRow {
  return {
    machine_sn: str(r.machine_sn),
    machine_name: str(r.machine_name, str(r.machine_sn)),
    state_category: toCategory(r.state_category),
    total_seconds: num(r.total_seconds),
    occurrences: num(r.occurrences),
  };
}

function parseBlocker(r: Record<string, unknown>): BlockerRow {
  return {
    blocked_machine_sn: str(r.blocked_machine_sn),
    blocked_machine_name: str(r.blocked_machine_name, str(r.blocked_machine_sn)),
    blocker_machine_sn: str(r.blocker_machine_sn),
    blocker_machine_name: str(r.blocker_machine_name, str(r.blocker_machine_sn)),
    blocked_seconds: num(r.blocked_seconds),
    episodes: num(r.episodes),
    first_blocked_at: str(r.first_blocked_at),
    blocker_down_from: str(r.blocker_down_from),
  };
}

function parseMachineRow(r: Record<string, unknown>): MachineSummaryRow {
  const status = str(r.status, 'no_data') as MachineStatus;
  return {
    machine_sn: str(r.machine_sn),
    machine_name: str(r.machine_name, str(r.machine_sn)),
    output: num(r.output),
    waste: num(r.waste),
    reject_rate: num(r.reject_rate),
    efficiency: num(r.efficiency),
    utilization: num(r.utilization),
    downtime_ms: num(r.downtime_ms),
    mtbf: num(r.mtbf),
    status: ['good', 'watch', 'bad', 'no_data'].includes(status)
      ? status
      : 'no_data',
  };
}

function parseTimelineSummary(
  r: Record<string, unknown> | undefined,
): TimelineSummary | null {
  if (!r) return null;
  return {
    total_faults: num(r.total_faults),
    total_fault_seconds: num(r.total_fault_seconds),
    internal_fault_seconds: num(r.internal_fault_seconds),
    external_fault_seconds: num(r.external_fault_seconds),
    critical_count: num(r.critical_count),
    major_count: num(r.major_count),
    minor_count: num(r.minor_count),
    longest_fault_seconds: num(r.longest_fault_seconds),
    machines_with_faults: num(r.machines_with_faults),
  };
}

function parseLostCause(r: Record<string, unknown>): LostCause {
  return {
    cause: str(r.cause, 'N/A'),
    rating: str(r.rating),
    lost_production: num(r.lost_production),
    stops: num(r.stops),
    total_stops_ms: num(r.total_stops_ms),
  };
}

function parseKpiPoint(r: Record<string, unknown>): KpiPoint {
  // Handle both wire formats: MySQL "2026-06-29 08:30:00.000000" and the NATS
  // service's ISO "2026-06-29T08:30:00Z". Parse the Y-M-D H:M:S components and
  // anchor to UTC so the wall-clock time is preserved regardless of separator
  // or trailing zone.
  const raw = str(r.bucket);
  const m = raw.match(
    /(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/,
  );
  const t = m
    ? Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], m[6] ? +m[6] : 0)
    : NaN;
  // Show the values as-is (they can be negative or well above 100% — the raw
  // production_period data is noisy). Only drop truly non-numeric readings.
  const numOrNull = (v: unknown): number | null => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return {
    t,
    efficiency: numOrNull(r.efficiency),
    utilization: numOrNull(r.utilization),
  };
}

function parseSpeedLoss(r: Record<string, unknown>): SpeedLoss {
  return {
    rating: str(r.rating),
    duration_ms: num(r.duration_ms),
    good_production: num(r.good_production),
    theoretical_prod: num(r.theoretical_prod),
    lost_production: num(r.lost_production),
  };
}

function parseLineSummary(
  r: Record<string, unknown> | undefined,
): LineSummary | null {
  if (!r) return null;
  return {
    machine_count: num(r.machine_count),
    avg_efficiency: num(r.avg_efficiency),
    min_efficiency: num(r.min_efficiency),
    max_efficiency: num(r.max_efficiency),
    avg_utilization: num(r.avg_utilization),
    total_waste: num(r.total_waste),
    total_downtime_ms: num(r.total_downtime_ms),
    good_count: num(r.good_count),
    watch_count: num(r.watch_count),
    bad_count: num(r.bad_count),
    no_data_count: num(r.no_data_count),
  };
}

export class ShiftAnalysisService {
  /**
   * Fetch the full shift analysis (all 4 statements in parallel).
   * @throws Error with a readable message if any statement fails.
   */
  static async getShiftAnalysis(
    lineBk: string,
    shiftName: string,
    shiftDate: string,
    token: string,
  ): Promise<ShiftAnalysis> {
    const params = {
      line_bk: lineBk,
      shift_name: shiftName,
      shift_date: shiftDate,
    };

    const [
      causesRes,
      culpritsRes,
      statusRes,
      statusInsightRes,
      attributionRes,
      timelineRes,
      timelineInsightRes,
      machinesRes,
      machinesInsightRes,
      lostRes,
      speedRes,
      kpiRes,
    ] = await Promise.all([
      orgNatsClient.request(StatementIds.SHIFT_STOP_CAUSES, params, token, {
        timeout: TIMEOUT,
      }),
      orgNatsClient.request(
        StatementIds.SHIFT_STOP_CAUSES_INSIGHT,
        params,
        token,
        { timeout: TIMEOUT },
      ),
      orgNatsClient.request(StatementIds.SHIFT_STATUS_TIMELINE, params, token, {
        timeout: TIMEOUT,
      }),
      orgNatsClient.request(
        StatementIds.SHIFT_STATUS_TIMELINE_INSIGHT,
        params,
        token,
        { timeout: TIMEOUT },
      ),
      orgNatsClient.request(StatementIds.SHIFT_STOP_ATTRIBUTION, params, token, {
        timeout: TIMEOUT,
      }),
      orgNatsClient.request(StatementIds.SHIFT_TIMELINE, params, token, {
        timeout: TIMEOUT,
      }),
      orgNatsClient.request(
        StatementIds.SHIFT_TIMELINE_INSIGHT,
        params,
        token,
        { timeout: TIMEOUT },
      ),
      orgNatsClient.request(
        StatementIds.SHIFT_MACHINE_SUMMARY,
        params,
        token,
        { timeout: TIMEOUT },
      ),
      orgNatsClient.request(
        StatementIds.SHIFT_MACHINE_SUMMARY_INSIGHT,
        params,
        token,
        { timeout: TIMEOUT },
      ),
      // Optional / graceful: not all machines populate the agg tables.
      orgNatsClient
        .request(StatementIds.SHIFT_LOST_PRODUCTION, params, token, {
          timeout: TIMEOUT,
        })
        .catch(() => ({ success: false }) as Awaited<ReturnType<typeof orgNatsClient.request>>),
      orgNatsClient
        .request(StatementIds.SHIFT_SPEED_LOSSES, params, token, {
          timeout: TIMEOUT,
        })
        .catch(() => ({ success: false }) as Awaited<ReturnType<typeof orgNatsClient.request>>),
      orgNatsClient
        .request(StatementIds.SHIFT_KPI_TIMELINE, params, token, {
          timeout: TIMEOUT,
        })
        .catch(() => ({ success: false }) as Awaited<ReturnType<typeof orgNatsClient.request>>),
    ]);

    // Surface the first failure with its readable reason.
    const failure = [
      causesRes,
      culpritsRes,
      statusRes,
      statusInsightRes,
      attributionRes,
      timelineRes,
      timelineInsightRes,
      machinesRes,
      machinesInsightRes,
    ].find((r) => !r.success);
    if (failure) {
      throw new Error(
        `${failure.error_code ?? 'ERROR'}: ${failure.error ?? 'Request failed'}`,
      );
    }

    const events = asRows(timelineRes.data).map(parseFaultEvent);
    const lanes = new Map<string, FaultEvent[]>();
    for (const e of events) {
      const arr = lanes.get(e.machine_sn) ?? [];
      arr.push(e);
      lanes.set(e.machine_sn, arr);
    }

    // Status timeline lanes (skip 0s transition rows).
    const stateLanes = new Map<string, StateInterval[]>();
    for (const r of asRows(statusRes.data)) {
      const iv = parseStateInterval(r);
      if (iv.duration_s <= 0) continue;
      const arr = stateLanes.get(iv.machine_sn) ?? [];
      arr.push(iv);
      stateLanes.set(iv.machine_sn, arr);
    }

    // Per-machine state breakdown.
    const stateBreakdown = new Map<string, StateBreakdownRow[]>();
    for (const r of asRows(statusInsightRes.data)) {
      const row = parseStateBreakdown(r);
      const arr = stateBreakdown.get(row.machine_sn) ?? [];
      arr.push(row);
      stateBreakdown.set(row.machine_sn, arr);
    }

    // Who blocked whom — grouped by victim, rows already worst-first.
    const blockers = new Map<string, BlockerRow[]>();
    for (const r of asRows(attributionRes.data)) {
      const row = parseBlocker(r);
      const arr = blockers.get(row.blocked_machine_sn) ?? [];
      arr.push(row);
      blockers.set(row.blocked_machine_sn, arr);
    }

    const kpiTimeline = kpiRes.success
      ? asRows(kpiRes.data)
          .map(parseKpiPoint)
          .filter((p) => Number.isFinite(p.t))
      : [];

    return {
      lineBk,
      shiftName,
      shiftDate,
      stopCauses: asRows(causesRes.data).map(parseStopCause),
      culpritMachines: asRows(culpritsRes.data).map(parseCulprit),
      stateLanes,
      stateBreakdown,
      blockers,
      events,
      lanes,
      timelineSummary: parseTimelineSummary(asRows(timelineInsightRes.data)[0]),
      machines: asRows(machinesRes.data).map(parseMachineRow),
      lineSummary: parseLineSummary(asRows(machinesInsightRes.data)[0]),
      lostCauses: lostRes.success ? asRows(lostRes.data).map(parseLostCause) : [],
      speedLosses: speedRes.success
        ? asRows(speedRes.data).map(parseSpeedLoss)
        : [],
      kpiTimeline,
    };
  }
}
